import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BaseNode } from '../hooks/useNearbyBaseNodes';
import InfoRow from './InfoRow';

/**
 * Expandable accordion for displaying individual Base Node details
 * Based on BaseNode.vue field structure
 */
interface BaseNodeAccordionProps {
  baseNode: BaseNode;
  index: number;
  isExpanded?: boolean;
}

const BaseNodeAccordion = ({
  baseNode,
  index,
  isExpanded = false,
}: BaseNodeAccordionProps) => {
  const {
    latitude,
    longitude,
    azimuth,
    downtilt,
    market,
    site,
    carrier,
    serial,
    hostname,
    sector,
    cell,
    subscribers,
    part_number,
    planning_id,
    height_agl,
    height_gps,
    region,
  } = baseNode;
  const formatLocation = (): string | null => {
    if (latitude && longitude) {
      return `${latitude}, ${longitude}`;
    }
    return null;
  };

  const formatAzimuthTilt = (): string | null => {
    if (azimuth !== 0 || downtilt !== 0) {
      return `${azimuth} / ${downtilt}`;
    }
    return null;
  };

  const formatMarketSite = (): string | null => {
    if (market || site) {
      return `${market} / ${site}`;
    }
    return null;
  };

  const formatCarrier = (): string | null => {
    if (carrier && carrier.length > 0) {
      const frequencies = carrier
        .filter((c) => c.frequency !== 0)
        .map((c) => {
          if (c.frequency >= 1000) {
            return `${(c.frequency / 1000).toFixed(1)} GHz`;
          }
          return `${c.frequency} MHz`;
        });
      return frequencies.length > 0 ? frequencies.join(', ') : null;
    }
    return null;
  };

  const displayName = serial || hostname || `BN ${index + 1}`;

  return (
    <Accordion sx={{ mb: 1 }} defaultExpanded={isExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {displayName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {hostname && <InfoRow label="Hostname" value={hostname} small />}
          {formatLocation() && (
            <InfoRow label="Location" value={formatLocation()} small />
          )}
          {sector && <InfoRow label="Sector" value={sector} small />}
          {formatAzimuthTilt() && (
            <InfoRow
              label="Azimuth / Tilt (°)"
              value={formatAzimuthTilt()}
              small
            />
          )}
          {cell && <InfoRow label="Cell" value={cell} small />}
          {height_agl !== null && height_agl !== undefined && (
            <InfoRow
              label="Height AGL (m)"
              value={height_agl.toString()}
              small
            />
          )}
          {formatMarketSite() && (
            <InfoRow label="Market / Site" value={formatMarketSite()} small />
          )}
          {height_gps !== null && height_gps !== undefined && (
            <InfoRow
              label="Height MSL (m)"
              value={height_gps.toString()}
              small
            />
          )}
          {region && <InfoRow label="Region" value={region} small />}
          {formatCarrier() && (
            <InfoRow label="Carriers (MHz)" value={formatCarrier()} small />
          )}
          {serial && <InfoRow label="Serial Number" value={serial} small />}
          {subscribers !== null && subscribers !== undefined && (
            <InfoRow label="Subscribers" value={subscribers.toString()} small />
          )}
          {part_number && (
            <InfoRow label="Part Number" value={part_number} small />
          )}
          {planning_id && (
            <InfoRow label="Planning ID" value={planning_id} small />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BaseNodeAccordion;
